import { Box, Button, Container, Typography } from '@mui/material'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRecoilValue } from 'recoil'

import { PricingCard } from '../components/pricing'
import { OfficeSelector } from '../components/subscription'
import { getAllTiers } from '../config/subscriptionTiers'
import { sessionState } from '../state'

import routes from './routes'

const Pricing = () => {
  const navigate = useNavigate()
  const session = useRecoilValue(sessionState)
  const tiers = getAllTiers()
  const [selectedTier, setSelectedTier] = useState<string | null>(null)
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'annual'>(
    'annual'
  )
  const [officeSelectorOpen, setOfficeSelectorOpen] = useState(false)

  const handleSelectTier = (tierId: string, interval: 'monthly' | 'annual') => {
    if (!session.user) {
      // Not signed in - redirect to signup
      navigate(routes.signup)
    } else if (tierId === 'free') {
      // Free tier - redirect to offices page (to create office or view existing)
      navigate(routes.offices)
    } else {
      // Paid tier - show office selector
      setSelectedTier(tierId)
      setBillingInterval(interval)
      setOfficeSelectorOpen(true)
    }
  }

  const handleCloseOfficeSelector = () => {
    setOfficeSelectorOpen(false)
    setSelectedTier(null)
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
        py: 8
      }}
    >
      <Container maxWidth='lg'>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant='h2'
            sx={{
              fontWeight: 800,
              color: '#ffffff',
              mb: 2,
              textShadow: '0 2px 10px rgba(0, 0, 0, 0.2)'
            }}
          >
            Choose Your Plan
          </Typography>
          <Typography
            variant='h5'
            sx={{
              color: 'rgba(255, 255, 255, 0.95)',
              fontWeight: 400
            }}
          >
            Scale your office capacity as your team grows
          </Typography>
        </Box>

        {/* Pricing Cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)'
            },
            gap: 3
          }}
        >
          {tiers.map(tier => {
            const isRecommended = tier.id === 'medium'

            return (
              <PricingCard
                key={tier.id}
                tier={tier}
                isRecommended={isRecommended}
                isSignedIn={!!session.user}
                billingInterval={billingInterval}
                onBillingIntervalChange={setBillingInterval}
                onSelect={handleSelectTier}
              />
            )
          })}
        </Box>

        {/* Footer Note */}
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography
            variant='body1'
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              mb: 2
            }}
          >
            All plans include unlimited desks and full booking features
          </Typography>
          {!session.user && (
            <Button
              variant='text'
              onClick={() => navigate(routes.signin)}
              sx={{
                color: '#ffffff',
                textDecoration: 'underline',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              Already have an account? Sign in
            </Button>
          )}
        </Box>
      </Container>

      {/* Office Selector Modal */}
      {session.user && selectedTier && (
        <OfficeSelector
          open={officeSelectorOpen}
          tierId={selectedTier}
          billingInterval={billingInterval}
          userId={session.user.uid || ''}
          onClose={handleCloseOfficeSelector}
        />
      )}
    </Box>
  )
}

export default Pricing
